import React, { useEffect } from 'react';
import { Menu } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCompactViewport, usePageAccess } from '@/hooks';
import VERSION from '../Version';
import { faAlignLeft, faAlignRight, Icon } from '../Icon';
import { SliderContent, SliderMenuWrap, VersionText } from './Slider.styled';

const normalizePath = (path: string) => {
  const withoutTrailingSlash = path.replace(/\/+$/, '');
  return withoutTrailingSlash.startsWith('/')
    ? withoutTrailingSlash || '/'
    : `/${withoutTrailingSlash}`;
};

type SliderProps = {
  variant?: 'rail' | 'panel';
  onNavigate?: () => void;
};

export const Slider = ({ variant = 'rail', onNavigate }: SliderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pages = usePageAccess();
  const compact = useCompactViewport();
  const [collapsed, setCollapsed] = React.useState(false);
  const routes = pages.filter((o) => o.enabled && !o.hideInMenu);
  const panel = variant === 'panel';
  const showCollapsed = !panel && collapsed;

  const visibleSubRoutes = (o: (typeof routes)[number]) =>
    o.subRoutes?.filter((r) => !r.hideInMenu && r.enabled !== false) ?? [];

  const currentPath = normalizePath(location.pathname);
  const isCurrent = (to: string) => {
    const targetPath = normalizePath(to);
    return (
      currentPath === targetPath ||
      (targetPath !== '/' && currentPath.startsWith(`${targetPath}/`))
    );
  };

  const items = routes.map((o) => {
    const children = visibleSubRoutes(o).map((r) => ({
      key: r.to,
      label: r.label,
      icon: <Icon icon={r.icon} />,
      color: r.color,
    }));

    return {
      key: o.to,
      label: o.label,
      icon: <Icon icon={o.icon} />,
      color: o.color,
      children: children.length ? children : undefined,
    };
  });

  const selectedKeys = routes.flatMap((o) => {
    const activeChild = visibleSubRoutes(o).find((r) => isCurrent(r.to));
    if (activeChild) {
      return [o.to, activeChild.to];
    }
    return isCurrent(o.to) || o.subRoutes?.some((r) => isCurrent(r.to))
      ? [o.to]
      : [];
  });

  const [openKeys, setOpenKeys] = React.useState<string[]>([]);

  const onChangeCollapsed = (status: boolean) => {
    setCollapsed(status);
    localStorage.setItem('collapsed', status.toString());
  };

  useEffect(() => {
    const savedCollapsed = localStorage.getItem('collapsed') === 'true';
    setCollapsed(savedCollapsed);
  }, []);

  if (!panel && compact) return null;

  return (
    <SliderContent $collapsed={showCollapsed} $panel={panel}>
      {panel ? null : (
        <div
          className={`d-flex flex-shrink-0 justify-content-${
            showCollapsed ? 'center' : 'end'
          } p-3 pb-0`}
        >
          <Icon
            className="hoverable-icon"
            icon={showCollapsed ? faAlignLeft : faAlignRight}
            onClick={() => onChangeCollapsed(!collapsed)}
          />
        </div>
      )}
      <SliderMenuWrap>
        <Menu
          mode="inline"
          inlineCollapsed={showCollapsed}
          items={items}
          className="border-0"
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          onSelect={(e) => {
            navigate(e.key);
            onNavigate?.();
          }}
        />
      </SliderMenuWrap>
      <VersionText $collapsed={showCollapsed}>{`v ${VERSION}`}</VersionText>
    </SliderContent>
  );
};
